/**
 * Animated data-network diagram (spec §75). Pure SVG + CSS; honours prefers-reduced-motion.
 * Text labels only — no third-party logos.
 */
const COLS = [
  { title: "PRIVATE DATA SOURCES", x: 90, items: ["PostgreSQL", "Oracle", "SQL Server", "MySQL", "ERP", "Warehouse"] },
  { title: "SECURE CONNECTORS", x: 290, items: ["Outbound-only", "Read-only account", "Local sanitisation"] },
  { title: "DBINDEX DATA NODES", x: 500, items: ["Product Data", "Financial Data", "Supply Chain", "Real Estate", "Market Data"] },
  { title: "GLOBAL DISCOVERY INDEX", x: 710, items: ["Manifests", "Capabilities", "Ranking"] },
  { title: "AI CONSUMERS", x: 910, items: ["ChatGPT", "Claude", "Gemini", "Enterprise Agents", "Applications"] },
] as const;

const TOP = 80;
const BOTTOM = 500;

function ys(n: number) {
  const step = (BOTTOM - TOP) / n;
  return Array.from({ length: n }, (_, i) => TOP + step * (i + 0.5));
}

export function DataNetwork({ className }: { className?: string }) {
  const pos = COLS.map((c) => ys(c.items.length));
  const edges: { x1: number; y1: number; x2: number; y2: number; k: string }[] = [];
  for (let c = 0; c < COLS.length - 1; c++) {
    const a = pos[c]!;
    const b = pos[c + 1]!;
    a.forEach((y1, i) => {
      // connect each node to 1-2 nodes in the next column (deterministic)
      const j1 = Math.round((i / Math.max(1, a.length - 1)) * (b.length - 1));
      const targets = new Set([j1, (j1 + (i % 2 === 0 ? 1 : b.length - 1)) % b.length]);
      targets.forEach((j) => edges.push({ x1: COLS[c]!.x + 70, y1, x2: COLS[c + 1]!.x - 70, y2: b[j]!, k: `${c}-${i}-${j}` }));
    });
  }
  return (
    <svg viewBox="0 0 1000 540" className={className} role="img" aria-labelledby="dn-title dn-desc">
      <title id="dn-title">DBIndex data network</title>
      <desc id="dn-desc">
        Private databases connect through outbound-only connectors to sanitised DBIndex data nodes, which are indexed globally for discovery by AI consumers.
      </desc>
      <defs>
        <linearGradient id="dn-edge" x1="0" x2="1">
          <stop offset="0" stopColor="var(--border-strong)" />
          <stop offset="1" stopColor="var(--accent)" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      {/* privacy boundary */}
      <rect x="12" y="44" width="360" height="480" rx="12" fill="none" stroke="var(--border-strong)" strokeDasharray="4 6" />
      <text x="24" y="36" fill="var(--text-3)" fontSize="11" fontFamily="var(--font-mono)">
        YOUR NETWORK — production DB never exposed
      </text>
      {edges.map((e, i) => {
        const mx = (e.x1 + e.x2) / 2;
        const d = `M${e.x1},${e.y1} C${mx},${e.y1} ${mx},${e.y2} ${e.x2},${e.y2}`;
        return (
          <g key={e.k}>
            <path d={d} fill="none" stroke="var(--border)" strokeWidth="1" />
            <path d={d} fill="none" stroke="url(#dn-edge)" strokeWidth="1.5" className="flow-path" style={{ animationDelay: `${(i % 7) * -0.23}s` }} />
          </g>
        );
      })}
      {COLS.map((c, ci) => (
        <g key={c.title}>
          <text x={c.x} y={TOP - 18} textAnchor="middle" fill="var(--text-3)" fontSize="10.5" letterSpacing="1.2" fontFamily="var(--font-mono)">
            {c.title}
          </text>
          {c.items.map((label, i) => {
            const y = pos[ci]![i]!;
            const accent = ci === 2 || ci === 3;
            return (
              <g key={label}>
                <rect x={c.x - 70} y={y - 17} width="140" height="34" rx="7" fill="var(--panel)" stroke={accent ? "var(--accent)" : "var(--border-strong)"} strokeOpacity={accent ? 0.55 : 1} />
                <circle cx={c.x - 56} cy={y} r="3" fill={accent ? "var(--accent)" : "var(--good)"} className="pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                <text x={c.x - 46} y={y + 4} fill="var(--text)" fontSize="12.5" fontFamily="var(--font-sans)">
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
}
